import CompanyService from "../../../../app/data/company/companyService";
import UserProfile from "../../../../app/data/user/UserProfile";
import { SearchRequestersResponseModel } from "../../../../app/data/company/models";
import { isXgsiEmailDomain } from "./bolShipperEmailUtils";

const companyService = CompanyService.getInstance();

export interface ResolvedRequesterTeam {
  teamId: string;
  teamName: string;
}

export const resolveRequesterTeamName = async (teamId: string): Promise<string | null> => {
  const response = await companyService.getTeamUsers(teamId, true, undefined);
  if (!response.ok()) return null;

  const users = response.data?.content ?? [];
  if (!Array.isArray(users) || users.length === 0) return null;

  const teamName = users[0]?.team?.name;
  return teamName?.trim() || null;
};

/** Pull a bare email from RFC5322-style values like `Name <user@example.com>`. */
export const extractEmailAddress = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  const bracketed = trimmed.match(/<([^>]+@[^>]+)>/);
  if (bracketed?.[1]) return bracketed[1].trim();
  const plain = trimmed.match(/[^\s<>]+@[^\s<>]+\.[^\s<>]+/);
  if (plain?.[0]) return plain[0].trim();
  return trimmed.includes("@") ? trimmed : undefined;
};

export const isEmailRegisteredOnTeam = async (
  teamId: string,
  email: string
): Promise<boolean> => {
  const normalized = extractEmailAddress(email)?.toLowerCase();
  if (!normalized) return false;

  const response = await companyService.getTeamUsers(teamId, true, normalized);
  if (!response.ok()) return false;

  const users = response.data?.content ?? [];
  if (!Array.isArray(users)) return false;

  return users.some((user: { email?: string; pendingEmail?: string; status?: string }) => {
    const registered = extractEmailAddress(user.email)?.toLowerCase();
    const pending = extractEmailAddress(user.pendingEmail)?.toLowerCase();
    return registered === normalized || pending === normalized;
  });
};

export const getEffectiveSenderEmail = (
  senderEmail?: string,
  forwardedSenderEmail?: string,
  fallbackSender?: string
): string | undefined => {
  const candidates = [
    extractEmailAddress(senderEmail),
    extractEmailAddress(forwardedSenderEmail),
    extractEmailAddress(fallbackSender),
  ].filter((email): email is string => !!email);

  const externalSender = candidates.find((email) => !isXgsiEmailDomain(email.toLowerCase()));
  return externalSender ?? candidates[0];
};

const matchesEmailAddress = (candidate: string | undefined, target: string): boolean => {
  const normalized = extractEmailAddress(candidate)?.toLowerCase();
  return !!normalized && normalized === target;
};

const pickRequesterTeamFromSearch = (
  search: SearchRequestersResponseModel | null
): ResolvedRequesterTeam | null => {
  if (!search) return null;

  const team = search.teams[0];
  if (team) {
    return { teamId: team.id, teamName: team.name };
  }

  const account = search.accounts[0];
  if (account) {
    return { teamId: account.id, teamName: account.name };
  }

  return null;
};

const resolveTeamFromCustomerUsers = async (email: string): Promise<ResolvedRequesterTeam | null> => {
  const response = await companyService.getAllUsers(undefined, {
    search: email,
    company: "",
    role: null,
    status: null,
  });
  if (!response.ok()) return null;

  const users: UserProfile[] = response.data?.content ?? [];
  if (!Array.isArray(users) || users.length === 0) return null;

  const matchedUser =
    users.find(
      (user) =>
        matchesEmailAddress(user.email, email) || matchesEmailAddress(user.pendingEmail, email)
    ) ?? (users.length === 1 ? users[0] : undefined);

  if (!matchedUser?.team?.id || !matchedUser.team.name) return null;

  return {
    teamId: matchedUser.team.id,
    teamName: matchedUser.team.name,
  };
};

/** When requester company is unset, look up the sender's customer team by email. */
export const resolveRequesterTeamFromSenderEmail = async (
  senderEmail?: string
): Promise<ResolvedRequesterTeam | null> => {
  const email = extractEmailAddress(senderEmail)?.toLowerCase();
  if (!email || isXgsiEmailDomain(email)) return null;

  const teamFromUsers = await resolveTeamFromCustomerUsers(email);
  if (teamFromUsers) return teamFromUsers;

  const requesterSearch = await companyService.getRequestersGlobalSearch(email);
  return pickRequesterTeamFromSearch(requesterSearch);
};
